import React from "react";
import { Modal } from "antd";

interface Props {
    open: boolean;
    onClose: () => void;
}

interface Node {
    id: string;
    label: string;
    x: number;
    y: number;
    kind: "active" | "pause" | "terminal";
}

const NODE_W = 168;
const NODE_H = 40;

// Vertical layout (viewBox 760 × 720). Three lanes:
//   x=110 LEFT (pause / terminal)
//   x=380 CENTER (active path)
//   x=650 RIGHT (escalated / terminal)
const NODES: Node[] = [
    { id: "Triage", label: "Triage", x: 380, y: 50, kind: "active" },
    { id: "Held", label: "Held", x: 110, y: 170, kind: "pause" },
    { id: "Negotiating", label: "Negotiating", x: 380, y: 170, kind: "active" },
    { id: "Escalated", label: "Escalated", x: 650, y: 170, kind: "pause" },
    {
        id: "AwaitingConfirmation",
        label: "Awaiting confirmation",
        x: 380,
        y: 320,
        kind: "active",
    },
    { id: "Executing", label: "Executing", x: 380, y: 460, kind: "active" },
    { id: "Closed", label: "Closed", x: 380, y: 600, kind: "terminal" },
    { id: "Reassigned", label: "Reassigned", x: 110, y: 600, kind: "terminal" },
    { id: "Walked", label: "Walked", x: 650, y: 600, kind: "terminal" },
];

const NODES_BY_ID = Object.fromEntries(NODES.map((n) => [n.id, n]));

type Side = "top" | "bottom" | "left" | "right";

interface Edge {
    from: string;
    to: string;
    label?: string;
    // Slight perpendicular offset for the auto-routed line so reverse edges
    // (Negotiating ↔ Escalated) don't overlap.
    perpOffset?: number;
    // Explicit anchor overrides (for cases pickSides would pick wrong).
    fromSide?: Side;
    toSide?: Side;
    // Explicit polyline. When set, the line goes through these absolute
    // points in order; the arrowhead is at the last point.
    points?: Array<{ x: number; y: number }>;
    // Optional explicit label position. When omitted, uses the segment
    // midpoint (or longest segment for polylines).
    labelAt?: { x: number; y: number };
}

const EDGES: Edge[] = [
    { from: "Triage", to: "Negotiating", label: "classified · autonomous" },
    {
        from: "Triage",
        to: "Held",
        label: "above cap",
        points: [
            { x: 296, y: 50 }, // Triage left edge
            { x: 110, y: 50 }, // corner
            { x: 110, y: 150 }, // Held top
        ],
        labelAt: { x: 200, y: 42 },
    },
    { from: "Held", to: "Negotiating", label: "keep · take" },
    { from: "Held", to: "Reassigned", label: "reassign" },
    { from: "Negotiating", to: "AwaitingConfirmation", label: "terms agreed" },
    {
        from: "Negotiating",
        to: "Escalated",
        label: "guardrail hit",
        perpOffset: -10,
    },
    {
        from: "Escalated",
        to: "Negotiating",
        label: "resume",
        perpOffset: -10,
    },
    {
        from: "AwaitingConfirmation",
        to: "Escalated",
        label: "e-sign band",
        points: [
            { x: 464, y: 320 }, // AC right edge
            { x: 650, y: 320 }, // corner
            { x: 650, y: 190 }, // Escalated bottom
        ],
        labelAt: { x: 660, y: 258 },
    },
    {
        from: "AwaitingConfirmation",
        to: "Executing",
        label: "supplier confirmed",
    },
    {
        from: "AwaitingConfirmation",
        to: "Reassigned",
        label: "hand to strategic",
        points: [
            { x: 296, y: 320 }, // AC left edge
            { x: 130, y: 320 }, // corner (offset 20px from Held column)
            { x: 130, y: 580 }, // Reassigned top (offset)
        ],
        labelAt: { x: 142, y: 460 },
    },
    { from: "Escalated", to: "Walked", label: "walk" },
    {
        from: "Escalated",
        to: "Reassigned",
        label: "reassign",
        points: [
            { x: 734, y: 170 }, // Escalated right edge
            { x: 752, y: 170 }, // horizontal step right to clear Walked
            { x: 752, y: 640 }, // straight down past Walked
            { x: 110, y: 640 }, // left under Walked and Closed
            { x: 110, y: 620 }, // up into Reassigned bottom
        ],
        labelAt: { x: 730, y: 420 },
    },
    { from: "Executing", to: "Closed" },
];

function anchorPoint(n: Node, side: Side): { x: number; y: number } {
    const cx = n.x;
    const cy = n.y;
    switch (side) {
        case "top":
            return { x: cx, y: cy - NODE_H / 2 };
        case "bottom":
            return { x: cx, y: cy + NODE_H / 2 };
        case "left":
            return { x: cx - NODE_W / 2, y: cy };
        case "right":
            return { x: cx + NODE_W / 2, y: cy };
    }
}

function pickSides(a: Node, b: Node): { from: Side; to: Side } {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (Math.abs(dy) >= Math.abs(dx)) {
        return dy > 0
            ? { from: "bottom", to: "top" }
            : { from: "top", to: "bottom" };
    }
    return dx > 0
        ? { from: "right", to: "left" }
        : { from: "left", to: "right" };
}

function perpShift(
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    offset: number
): [{ x: number; y: number }, { x: number; y: number }] {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    const sx = nx * offset;
    const sy = ny * offset;
    return [
        { x: p1.x + sx, y: p1.y + sy },
        { x: p2.x + sx, y: p2.y + sy },
    ];
}

const nodeFill = (kind: Node["kind"]): string => {
    switch (kind) {
        case "active":
            return "#e6f4ff";
        case "pause":
            return "#fff5e6";
        case "terminal":
            return "#f0f0f0";
    }
};

const nodeStroke = (kind: Node["kind"]): string => {
    switch (kind) {
        case "active":
            return "#1890ff";
        case "pause":
            return "#fa8c16";
        case "terminal":
            return "#8c8c8c";
    }
};

const legendSwatch = (kind: Node["kind"]): React.CSSProperties => ({
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: 3,
    background: nodeFill(kind),
    border: `1.5px solid ${nodeStroke(kind)}`,
    marginRight: 8,
    verticalAlign: "-2px",
});

interface RenderedEdge {
    points: Array<{ x: number; y: number }>;
    label?: string;
    labelAt?: { x: number; y: number };
}

function resolveEdge(e: Edge): RenderedEdge {
    if (e.points && e.points.length >= 2) {
        return { points: e.points, label: e.label, labelAt: e.labelAt };
    }
    const a = NODES_BY_ID[e.from];
    const b = NODES_BY_ID[e.to];
    const sides = pickSides(a, b);
    const fromSide = e.fromSide ?? sides.from;
    const toSide = e.toSide ?? sides.to;
    let p1 = anchorPoint(a, fromSide);
    let p2 = anchorPoint(b, toSide);
    if (e.perpOffset) {
        [p1, p2] = perpShift(p1, p2, e.perpOffset);
    }
    return { points: [p1, p2], label: e.label, labelAt: e.labelAt };
}

function pointsToPath(pts: Array<{ x: number; y: number }>): string {
    return pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
}

function defaultLabelAt(pts: Array<{ x: number; y: number }>): { x: number; y: number } {
    // For 2-point lines, use midpoint. For polylines, use midpoint of the
    // longest segment.
    if (pts.length === 2) {
        return { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
    }
    let bestLen = -1;
    let best = { x: pts[0].x, y: pts[0].y };
    for (let i = 1; i < pts.length; i++) {
        const dx = pts[i].x - pts[i - 1].x;
        const dy = pts[i].y - pts[i - 1].y;
        const len = Math.hypot(dx, dy);
        if (len > bestLen) {
            bestLen = len;
            best = { x: (pts[i].x + pts[i - 1].x) / 2, y: (pts[i].y + pts[i - 1].y) / 2 };
        }
    }
    return best;
}

export const StageDiagram: React.FC<Props> = ({ open, onClose }) => (
    <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={840}
        title="Negotiation stages"
    >
        <svg
            viewBox="0 0 780 660"
            style={{ width: "100%", height: "auto", display: "block" }}
        >
            <defs>
                <marker
                    id="arrow"
                    viewBox="0 0 10 10"
                    refX="9"
                    refY="5"
                    markerWidth="8"
                    markerHeight="8"
                    orient="auto-start-reverse"
                >
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#8c8c8c" />
                </marker>
            </defs>

            {EDGES.map((e, i) => {
                const re = resolveEdge(e);
                const labelPos = re.labelAt ?? defaultLabelAt(re.points);
                return (
                    <g key={i}>
                        <path
                            d={pointsToPath(re.points)}
                            fill="none"
                            stroke="#8c8c8c"
                            strokeWidth={1.5}
                            markerEnd="url(#arrow)"
                        />
                        {re.label && (
                            <text
                                x={labelPos.x}
                                y={labelPos.y - 4}
                                textAnchor="middle"
                                fontSize="11"
                                fill="#595959"
                                style={{
                                    paintOrder: "stroke",
                                    stroke: "#fff",
                                    strokeWidth: 4,
                                }}
                            >
                                {re.label}
                            </text>
                        )}
                    </g>
                );
            })}

            {NODES.map((n) => (
                <g key={n.id}>
                    <rect
                        x={n.x - NODE_W / 2}
                        y={n.y - NODE_H / 2}
                        width={NODE_W}
                        height={NODE_H}
                        rx={6}
                        fill={nodeFill(n.kind)}
                        stroke={nodeStroke(n.kind)}
                        strokeWidth={1.5}
                    />
                    <text
                        x={n.x}
                        y={n.y + 4}
                        textAnchor="middle"
                        fontSize="13"
                        fontWeight={600}
                        fill="#262626"
                    >
                        {n.label}
                    </text>
                </g>
            ))}
        </svg>
        <div
            style={{
                marginTop: 16,
                fontSize: 12,
                color: "#595959",
                display: "flex",
                justifyContent: "center",
                gap: 32,
            }}
        >
            <span>
                <span style={legendSwatch("active")} />
                Active negotiation
            </span>
            <span>
                <span style={legendSwatch("pause")} />
                Paused for a human
            </span>
            <span>
                <span style={legendSwatch("terminal")} />
                Terminal outcome
            </span>
        </div>
    </Modal>
);
